// New RFI / SI, notice or NDA in a large window: the document on the left, the form on the right. Drop a PDF, photo,
// Word file or email (or paste the text) — Claude reads it and fills what it can (purple, with where it found it).
// Nothing is saved until the PM presses Save; the history says "filled by Claude from <file> — checked".
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, viewUrl, write } from '../../lib/api';
import { uploadFile } from '../../lib/files';
import { supabase } from '../../lib/supabase';
import { isClosed, isSiType, statusLabel, TYPE_LOOK, type ItemType, type RegisterDef } from '../../lib/registers';
import { fileTag } from '../../lib/fileType';
import { Button, errText, useToast } from '../kit';
import { fijiVat, toVep, toVip } from '../../lib/vat';
import { fmtMoney } from '../../lib/format';
import { addDays, matchName, matchPerson, sameRef, words, type Contact, type NamedThing } from './match';
import { C3Loader, C3Busy } from '../C3Loader';

type Org = NamedThing & { kind: string | null };
type Read = {
  type: string;
  doc_type: string | null;
  doc_type_reason: string | null;
  subtype: string | null;
  topic: string | null;
  ref: string | null;
  letter_text: string | null;
  doc_date: string | null;
  respond_by: string | null;
  buildings: string[];
  from_company: string | null;
  to_company: string | null;
  to_person: string | null;
  drawings: string[];
  attachments: string[];
  answers_refs?: string[];
  delay_since: string | null;
  possible_eot: boolean | null;
  eot_days_claimed: number | null;
  company: string | null;
  signer_name: string | null;
  signer_role: string | null;
  date_signed: string | null;
  purpose: string | null;
  term_end: string | null;
  signed_by_both: boolean | null;
  amount_vep?: number | null;
  amount_vip?: number | null;
  claim_no?: number | null;
  period_to?: string | null;
  contract_ref?: string | null;
  sources: Record<string, string>;
  notes: string | null;
};
type Contract = { id: string; title: string | null; contract_no: string | null; project_no: string | null; building_id: string | null; building_ids: string[] | null; contractor_org_id: string | null };
type Call = { id: string; phase: string; error: string | null; output: Read | null };
type Lite = { id: string; type: ItemType; subtype?: string | null; number: string | null; title: string; status: string; ref_no: string | null; data: Record<string, unknown> | null; building_ids: string[] };
type Form = {
  subtype: string;
  title: string;
  ref: string;
  description: string;
  building_ids: string[];
  org_id: string;
  directed_to: string[];
  submitted_on: string;
  respond_by: string;
  onus_org_id: string;
  official: boolean;
  drawings: string;
  delay_since: string;
  possible_eot: boolean;
  eot_days_claimed: string;
  related: string[];
  /** SI: the RFIs / earlier SIs it answers (ids) */
  answers: string[];
  /** SI: an RFI was raised on site (unofficially) → RFI stages */
  si_on_site: boolean;
  company: string;
  company_org_id: string;
  signer: string;
  signer_role: string;
  date_signed: string;
  purpose: string;
  term_end: string;
  signed: boolean;
  /** variations / claims (V0.4.13): amount in the register's basis (variations VEP, claims VIP) */
  amount: string;
  claim_no: string;
  period_to: string;
  contract_id: string;
  deposit: boolean;
};

const A_NAME: Record<ItemType, string> = { rfi: 'an RFI', notice: 'a notice', nda: 'an NDA', variation: 'a variation', claim: 'a claim' };
const DROP: Record<ItemType, string> = { rfi: 'RFI letter', notice: 'notice letter', nda: 'signed NDA', variation: 'variation quote / VPR', claim: 'progress claim' };
const today = () => new Date().toISOString().slice(0, 10);
const REF_KEY: Partial<Record<ItemType, string>> = { rfi: 'Project RFI #', notice: 'Project NOPD #' };
const OFFICIAL_KEY: Partial<Record<ItemType, string>> = { rfi: 'Official RFI Doc sent', notice: 'Official Doc sent' };
const NOTICE_SUBS = ['NOPD Notice of Potential Delay', '(EOT) Extension of Time', '(GC) General Communication', '(DT) Document Transfer'];
const SUB_LABEL = (s: string) => (s.startsWith('NOPD') ? 'NOPD — Notice of Potential Delay' : s.startsWith('(EOT)') ? 'EOT — Extension of Time' : s.startsWith('(GC)') ? 'GC — General Communication' : s.startsWith('(DT)') ? 'DT — Document Transfer' : s);
const PREVIEWABLE = (mime: string | null, name: string) => /pdf|^image\//i.test(mime ?? '') || /\.(pdf|jpe?g|png|gif|webp)$/i.test(name);

const blank = (type: ItemType, subtypes: string[] | undefined): Form => ({
  subtype: type === 'notice' ? NOTICE_SUBS[0] : subtypes?.[0] ?? '',
  title: '',
  ref: '',
  description: '',
  building_ids: [],
  org_id: '',
  directed_to: [],
  submitted_on: today(),
  respond_by: addDays(today(), 7),
  onus_org_id: '',
  official: true,
  drawings: '',
  delay_since: '',
  possible_eot: false,
  eot_days_claimed: '',
  related: [],
  answers: [],
  si_on_site: false,
  company: '',
  company_org_id: '',
  signer: '',
  signer_role: '',
  date_signed: '',
  purpose: '',
  term_end: '',
  signed: true,
  amount: '',
  claim_no: '',
  period_to: '',
  contract_id: '',
  deposit: false,
});

export function NewItemWindow({
  def,
  siteId,
  files: queueIn,
  onClose,
  onSaved,
  preset,
}: {
  def: RegisterDef;
  /** start values, e.g. a follow-up SI from an SI page: { subtype: 'Architects SI', answers: [that SI's id] } */
  preset?: { subtype?: string; answers?: string[] };
  siteId: string;
  /** files dropped on the register (one item each, one after the other) */
  files: File[];
  onClose: () => void;
  onSaved: (id: string, more: boolean) => void;
}) {
  const toast = useToast();
  const type = def.type;
  const look = TYPE_LOOK[type];
  const input = useRef<HTMLInputElement>(null);
  const [queue, setQueue] = useState<File[]>(queueIn);
  const [done, setDone] = useState(0);
  const [phase, setPhase] = useState<'empty' | 'paste' | 'uploading' | 'reading' | 'filled' | 'manual'>('empty');
  const [file, setFile] = useState<{ id: string; name: string; mime: string | null } | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [callId, setCallId] = useState<string | null>(null);
  const [read, setRead] = useState<Read | null>(null);
  const fresh = (): Form => ({ ...blank(type, def.subtypes), ...(preset?.subtype ? { subtype: preset.subtype } : {}), ...(preset?.answers ? { answers: preset.answers } : {}) });
  const [f, setF] = useState<Form>(fresh);
  const [ai, setAi] = useState<Set<string>>(new Set());
  const [hot, setHot] = useState<string | null>(null);
  const [paste, setPaste] = useState('');
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [buildings, setBuildings] = useState<NamedThing[]>([]);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [items, setItems] = useState<Lite[]>([]);
  const [others, setOthers] = useState<Lite[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const money = type === 'variation' || type === 'claim';
  const basis = def.money ?? 'VEP';

  useEffect(() => {
    void (async () => {
      const [b, o, c] = await Promise.all([
        supabase.from('buildings').select('id, name, short_code, aliases').eq('site_id', siteId).is('deleted_at', null).order('sort').order('name'),
        supabase.from('organisations').select('id, name, short_code, aliases, kind').is('deleted_at', null).order('name'),
        supabase.from('contacts').select('id, org_id, full_name').is('deleted_at', null),
      ]);
      setBuildings((b.data ?? []) as NamedThing[]);
      setOrgs((o.data ?? []) as Org[]);
      setContacts((c.data ?? []) as Contact[]);
      const out: Lite[] = [];
      for (let from = 0; from < 10000; from += 1000) {
        const { data } = await supabase.from('items').select('id, type, subtype, number, title, status, ref_no, data, building_ids').eq('site_id', siteId).eq('type', type).is('deleted_at', null).order('id').range(from, from + 999);
        out.push(...((data ?? []) as Lite[]));
        if (!data || data.length < 1000) break;
      }
      setItems(out);
      if (type === 'claim') {
        const { data } = await supabase.from('contracts').select('id, title, contract_no, project_no, building_id, building_ids, contractor_org_id').eq('site_id', siteId).eq('has_claims', true).is('deleted_at', null);
        setContracts((data ?? []) as Contract[]);
      }
      if (type === 'notice') {
        const { data } = await supabase.from('items').select('id, type, number, title, status, ref_no, data, building_ids').eq('site_id', siteId).in('type', ['rfi', 'variation', 'notice']).is('deleted_at', null).not('status', 'in', '(closed,void,rejected,complete,paid)').limit(1000);
        setOthers((data ?? []) as Lite[]);
      }
    })();
  }, [siteId, type]);

  const swi = orgs.find((o) => o.short_code === 'SWI' || o.kind === 'internal');
  const pbs = orgs.find((o) => o.short_code === 'PBS');
  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setF((x) => ({ ...x, [k]: v }));
    setAi((s) => {
      if (!s.has(k)) return s;
      const n = new Set(s);
      n.delete(k);
      return n;
    });
  };

  // start on the next queued file
  useEffect(() => {
    if (phase === 'empty' && queue.length && orgs.length) void start(queue[0]);
  }, [queue, orgs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  async function start(fl: File) {
    setErr(null);
    setPhase('uploading');
    setBusy(`Uploading ${fl.name}…`);
    try {
      const r = await uploadFile(fl, { siteId, notes: `For a new ${def.one}` });
      const row = r.row as { id: string; original_name: string; mime: string | null };
      setFile({ id: row.id, name: row.original_name ?? fl.name, mime: row.mime ?? fl.type });
      if (PREVIEWABLE(row.mime ?? fl.type, fl.name)) setPreview(URL.createObjectURL(fl));
      setPhase('reading');
      setBusy(null);
      const res = await api<{ ai_call_id: string }>('item-read', { site_id: siteId, type, file_id: row.id });
      setCallId(res.ai_call_id);
    } catch (e) {
      setErr(errText(e));
      setPhase('manual');
      setBusy(null);
    }
  }

  // wait for Claude
  useEffect(() => {
    if (!callId || phase !== 'reading') return;
    let stop = false;
    const poll = async () => {
      const { data } = await supabase.from('ai_calls').select('id, phase, error, output').eq('id', callId).maybeSingle();
      const c = data as Call | null;
      if (stop || !c) return;
      if (c.phase === 'done' && c.output) {
        setRead(c.output);
        apply(c.output);
        setPhase('filled');
      } else if (c.phase === 'failed') {
        setErr(`SWI AI could not read it: ${c.error ?? 'unknown error'} — fill it in by hand.`);
        setPhase('manual');
      }
    };
    void poll();
    const t = setInterval(() => void poll(), 2500);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [callId, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  function apply(r: Read) {
    const filled = new Set<string>();
    const n: Form = fresh();
    const put = <K extends keyof Form>(k: K, v: Form[K] | null | undefined | '') => {
      if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return;
      n[k] = v as Form[K];
      filled.add(k);
    };
    const subs = type === 'notice' ? NOTICE_SUBS : def.subtypes ?? [];
    if (r.subtype && subs.includes(r.subtype)) put('subtype', r.subtype);
    put('title', r.topic ?? undefined);
    put('ref', r.ref ?? undefined);
    put('description', r.letter_text ?? undefined);
    put('building_ids', [...new Set(r.buildings.map((b) => matchName(b, buildings)?.id).filter((x): x is string => Boolean(x)))]);
    const from = matchName(r.from_company, orgs) ?? (type !== 'nda' && /pbs|pacific building/i.test(r.letter_text ?? '') ? pbs : null);
    if (from) put('org_id', from.id);
    const toOrg = matchName(r.to_company, orgs) ?? (r.to_person ? orgs.find((o) => o.id === matchPerson(r.to_person, contacts)?.org_id) : null);
    if (toOrg) put('directed_to', [toOrg.id]);
    put('submitted_on', r.doc_date ?? undefined);
    const base = r.doc_date ?? today();
    n.respond_by = r.respond_by ?? addDays(base, 7);
    if (r.respond_by) filled.add('respond_by');
    // whoever has to answer: the addressee (a notice to SWI → SWI)
    const onus = toOrg ?? (type === 'notice' ? swi : null);
    if (onus) put('onus_org_id', onus.id);
    put('drawings', r.drawings.join(' · '));
    if (type === 'rfi' && isSiType(n.subtype)) {
      // an SI goes from the architect / engineer to the contractor, who has the next move
      if (!toOrg && pbs) put('directed_to', [pbs.id]);
      if (!n.onus_org_id && n.directed_to[0]) put('onus_org_id', n.directed_to[0]);
      const hit = items.filter((i) => !isClosed(i.status) && (r.answers_refs ?? []).some((ref) => sameRef(ref, String(i.data?.['Project RFI #'] ?? '')) || sameRef(ref, i.ref_no) || sameRef(ref, i.number)));
      if (hit.length) put('answers', [...new Set([...n.answers, ...hit.map((i) => i.id)])]);
    }
    put('delay_since', r.delay_since ?? undefined);
    if (r.possible_eot !== null) put('possible_eot', Boolean(r.possible_eot));
    if (r.eot_days_claimed !== null) put('eot_days_claimed', String(r.eot_days_claimed));
    if (type === 'notice' && /\bEOT\b|extension of time/i.test(r.ref ?? '') && !r.subtype) put('subtype', NOTICE_SUBS[1]);
    if (money) {
      // variations / claims come from the contractor to SWI, who has the next move
      if (!from && pbs) put('org_id', pbs.id);
      if (!toOrg && swi) put('directed_to', [swi.id]);
      if (swi) put('onus_org_id', swi.id);
      const date = r.doc_date ?? today();
      const vat = fijiVat(date);
      const amt = basis === 'VEP' ? (r.amount_vep ?? (r.amount_vip != null ? toVep(r.amount_vip, vat) : null)) : (r.amount_vip ?? (r.amount_vep != null ? toVip(r.amount_vep, vat) : null));
      if (amt != null) put('amount', String(amt));
      if (r.claim_no != null) put('claim_no', String(r.claim_no));
      put('period_to', r.period_to ?? undefined);
      if (type === 'claim') {
        if (!r.ref && r.claim_no != null) put('ref', `Claim ${r.claim_no}`);
        const ref = (r.contract_ref ?? '').replace(/\s+/g, '').toLowerCase();
        const onB = (c: Contract) => (c.building_ids ?? (c.building_id ? [c.building_id] : [])).some((b) => n.building_ids.includes(b));
        const hit = (ref && contracts.find((c) => [c.contract_no, c.project_no].some((x) => (x ?? '').replace(/\s+/g, '').toLowerCase() === ref))) || contracts.find(onB);
        if (hit) put('contract_id', hit.id);
      }
    }
    if (type === 'nda') {
      put('company', r.company ?? undefined);
      const co = matchName(r.company, orgs);
      if (co) put('company_org_id', co.id);
      put('title', r.company ?? undefined);
      put('signer', r.signer_name ?? undefined);
      put('signer_role', r.signer_role ?? undefined);
      put('date_signed', r.date_signed ?? undefined);
      put('purpose', r.purpose ?? undefined);
      put('term_end', r.term_end ?? undefined);
      if (r.signed_by_both !== null) put('signed', Boolean(r.signed_by_both));
      const by = matchName(r.from_company, orgs) ?? (/pbs|pacific building/i.test(`${r.purpose ?? ''} ${r.letter_text ?? ''}`) ? pbs : null);
      if (by) put('org_id', by.id);
    }
    setF(n);
    setAi(filled);
    setHot(null);
  }

  // checks
  const dupe = useMemo(() => {
    if (type === 'nda') {
      const name = (f.company || f.title).toLowerCase().trim();
      return name ? items.find((i) => i.title.toLowerCase().trim() === name) ?? null : null;
    }
    if (!f.ref) return null;
    if (type === 'claim' && f.claim_no) {
      const c = items.find((i) => sameRef(i.ref_no, f.ref) || sameRef(i.ref_no, `Claim ${f.claim_no}`));
      if (c && (!f.building_ids.length || f.building_ids.some((b) => c.building_ids?.includes(b)))) return c;
      return null;
    }
    return items.find((i) => sameRef(String(i.data?.[REF_KEY[type] ?? ''] ?? ''), f.ref) || sameRef(i.ref_no, f.ref)) ?? null;
  }, [f.ref, f.company, f.title, items, type]);
  const nextNo = useMemo(() => {
    let best: { n: number; no: string } | null = null;
    for (const i of items) {
      const m = /^(.*?)(\d+)$/.exec(i.number ?? '');
      if (m && (!best || Number(m[2]) > best.n)) best = { n: Number(m[2]), no: i.number! };
    }
    if (!best) return null;
    const m = /^(.*?)(\d+)$/.exec(best.no)!;
    return m[1] + String(best.n + 1).padStart(Math.max(2, m[2].length), '0');
  }, [items]);
  const suggestions = useMemo(() => {
    if (type !== 'notice' || phase !== 'filled') return [];
    const w = words(`${f.title} ${f.description}`);
    return others
      .map((i) => {
        const iw = words(i.title);
        let score = 0;
        iw.forEach((x) => w.has(x) && score++);
        if (f.building_ids.some((b) => i.building_ids?.includes(b))) score += 0.5;
        return { i, score };
      })
      .filter((x) => x.score >= 1.5)
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map((x) => x.i);
  }, [others, f.title, f.description, f.building_ids, phase, type]);

  // SI: open RFIs / SIs it may answer — Claude's matches and the closest topics first
  const [siQ, setSiQ] = useState('');
  const siOn = type === 'rfi' && isSiType(f.subtype);
  const siList = useMemo(() => {
    if (!siOn) return [];
    const q = siQ.trim().toLowerCase();
    const w = words(`${f.title} ${f.description}`);
    return items
      .filter((i) => f.answers.includes(i.id) || (!isClosed(i.status) && i.status !== 'void'))
      .filter((i) => !q || `${i.number ?? ''} ${i.ref_no ?? ''} ${String(i.data?.['Project RFI #'] ?? '')} ${i.title}`.toLowerCase().includes(q))
      .map((i) => {
        let score = f.answers.includes(i.id) ? 100 : 0;
        words(i.title).forEach((x) => w.has(x) && score++);
        if (f.building_ids.some((b) => i.building_ids?.includes(b))) score += 0.5;
        return { i, score, n: Number(/(\d+)$/.exec(i.number ?? '')?.[1] ?? 0) };
      })
      .sort((a, b) => b.score - a.score || b.n - a.n)
      .slice(0, q ? 30 : 6)
      .map((x) => x.i);
  }, [siOn, siQ, items, f.answers, f.title, f.description, f.building_ids]);
  const orgName = (id: string | undefined) => (id ? orgs.find((o) => o.id === id)?.short_code ?? orgs.find((o) => o.id === id)?.name : undefined);

  const otherAmt = useMemo(() => {
    const v = Number(f.amount.replace(/,/g, ''));
    if (!money || f.amount.trim() === '' || !Number.isFinite(v)) return null;
    const vat = fijiVat(f.submitted_on || today());
    return basis === 'VEP' ? toVip(v, vat) : toVep(v, vat);
  }, [money, f.amount, f.submitted_on, basis]);

  const filledN = ai.size;
  const ready = Boolean((type === 'nda' ? f.company || f.title : f.title).trim());

  async function save(after: 'open' | 'next' | 'draft') {
    if (!ready) return toast({ tone: 'error', text: type === 'nda' ? 'Add the company' : 'Add a short topic' });
    setBusy('Saving…');
    const batch = crypto.randomUUID();
    const tag = file && read ? ` — filled by SWI AI from ${file.name}, checked` : '';
    const si = type === 'rfi' && isSiType(f.subtype);
    const amt = money && f.amount.trim() !== '' && Number.isFinite(Number(f.amount.replace(/,/g, ''))) ? Number(f.amount.replace(/,/g, '')) : null;
    const siRfi = si && !f.answers.length && f.si_on_site;
    try {
      const data: Record<string, unknown> = {};
      let orgId = f.org_id || null;
      let orgRaw: string | null = null;
      let title = f.title.trim();
      let notes: string | null = null;
      let status = def.statuses[0];
      if (type === 'rfi' || type === 'notice') {
        if (f.ref) data[REF_KEY[type]!] = f.ref.trim();
        data[OFFICIAL_KEY[type]!] = f.official;
        if (type === 'rfi' && f.drawings) data['Drawings referred to'] = f.drawings;
        if (si) {
          data['SI flow'] = siRfi ? 'rfi' : 'si';
          if (f.answers.length) data['Answers'] = f.answers.map((id) => items.find((i) => i.id === id)?.number ?? id);
          if (!siRfi) status = 'pending';
        }
        if (type === 'notice') {
          data['Document Date'] = f.submitted_on || null;
          if (f.delay_since) data['Delay since'] = f.delay_since;
          data['Possible EOT'] = f.possible_eot;
          if (f.related.length) data['Related items'] = f.related.map((id) => others.find((o) => o.id === id)?.number ?? id);
        }
        if (!orgId && read?.from_company) orgRaw = read.from_company;
      } else if (money) {
        if (!orgId && read?.from_company) orgRaw = read.from_company;
      } else if (type === 'nda') {
        // NDA: the company is the item; add it to Organisations when it's new
        title = (f.company || f.title).trim();
        let co = f.company_org_id || '';
        if (!co) {
          try {
            const row = await write<{ id: string }>('organisations', 'insert', { batch_id: batch, values: { name: title, kind: 'supplier', aliases: [], notes: f.purpose || null }, summary: `Added ${title} (from a new NDA)` });
            co = row.id;
          } catch {
            /* no permission for Organisations — keep the name on the NDA */
          }
        }
        orgId = co || null;
        if (!co) orgRaw = title;
        data['Person Signed'] = [f.signer, f.signer_role].filter(Boolean).join(' · ') || null;
        data['Date Signed'] = f.date_signed || null;
        data['Signed Attachment'] = Boolean(file) && f.signed;
        data['Saved in Drive'] = false;
        if (f.term_end) data['Ends'] = f.term_end;
        if (f.org_id && f.org_id !== co) data['Introduced by'] = orgs.find((o) => o.id === f.org_id)?.name ?? null;
        notes = f.purpose || null;
        status = f.signed ? 'signed' : 'pending';
      }
      const values: Record<string, unknown> = {
        site_id: siteId,
        type,
        subtype: type === 'nda' ? null : f.subtype || null,
        title,
        description: type === 'nda' ? null : f.description || null,
        status,
        building_ids: f.building_ids,
        buildings_raw: (read?.buildings ?? []).filter((b) => !matchName(b, buildings)),
        org_id: orgId,
        org_raw: orgRaw,
        directed_to: type === 'nda' ? [] : f.directed_to,
        submitted_on: type === 'nda' ? f.date_signed || null : f.submitted_on || null,
        respond_by: def.hasRespondBy ? f.respond_by || null : null,
        onus_org_id: type === 'nda' ? null : f.onus_org_id || (si && !siRfi ? f.directed_to[0] || pbs?.id || null : null) || null,
        stage: si && !siRfi ? 'issued' : null,
        eot_days_claimed: type === 'notice' && f.eot_days_claimed ? Number(f.eot_days_claimed) : null,
        ...(money ? { amount: amt, amount_basis: basis, ref_no: f.ref.trim() || null } : {}),
        notes,
        data,
      };
      const row = await write<{ id: string; number: string }>('items', 'insert', { batch_id: batch, values, summary: `New ${def.one}${tag}` });
      // a claim linked to its contract straight away (same as "Set up this claim" on the claim page)
      if (type === 'claim' && f.contract_id)
        await write('claim_details', 'insert', {
          batch_id: batch,
          values: { item_id: row.id, contract_id: f.contract_id, claim_no: f.claim_no ? Number(f.claim_no) : null, period_to: f.period_to || null, pbs_total_vip: amt, is_deposit: f.deposit },
          summary: `Set up the claim (contract, claim no.${f.deposit ? ', deposit claim' : ''})`,
        });
      if (file) await write('files', 'update', { id: file.id, batch_id: batch, values: { linked_table: 'items', linked_id: row.id, notes: read ? 'Original document — read by SWI AI' : null }, summary: `Attached ${file.name}` });
      // the RFIs / earlier SIs this SI answers: a final response from the issuer, then closed — same batch
      for (const id of si ? f.answers : []) {
        const a = items.find((i) => i.id === id);
        if (!a) continue;
        await write('item_responses', 'insert', {
          batch_id: batch,
          values: { item_id: a.id, kind: 'response', from_org_id: orgId, body: `Answered by ${row.number ?? 'a new SI'} — ${title}`, via: 'letter', received_on: f.submitted_on || today(), is_final: true, sets_onus: true, onus_org_id: null },
          summary: `Answered by ${row.number ?? 'SI'}`,
        });
        await write('items', 'update', { id: a.id, batch_id: batch, values: { status: 'closed', stage: 'closed', data: { ...(a.data ?? {}), 'Answered by SI': row.number ?? title } }, summary: `Closed — answered by ${row.number ?? 'SI'}` });
      }
      if (callId && read) await api('item-read', { ai_call_id: callId, item_id: row.id, confirm: true }).catch(() => undefined);
      toast({ tone: 'ok', text: `${row.number ?? def.one} saved` });
      const rest = queue.slice(1);
      if (after === 'next' || rest.length) {
        // next file in the queue, or an empty window for the next one
        setQueue(rest);
        setDone((d) => d + 1);
        reset();
        onSaved(row.id, true);
      } else onSaved(row.id, after === 'draft');
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }

  function reset() {
    if (preview) URL.revokeObjectURL(preview);
    setPhase('empty');
    setFile(null);
    setPreview(null);
    setCallId(null);
    setRead(null);
    setF(fresh());
    setAi(new Set());
    setErr(null);
    setPaste('');
  }

  async function usePaste() {
    if (!paste.trim()) return;
    const fl = new File([paste], 'Pasted email.txt', { type: 'text/plain' });
    await start(fl);
  }

  // where Claude found a value: jump the document to that page and outline the field
  const src = (k: string, srcKey = k) => {
    const s = read?.sources?.[srcKey];
    if (!ai.has(k)) return null;
    return (
      <button
        type="button"
        className="ni-src"
        title="Show where SWI AI found this"
        onClick={() => {
          setHot(k);
          const p = /(\d+)/.exec(s ?? '')?.[1];
          if (p) setPage(Number(p));
        }}
      >
        {s ?? 'SWI AI'}
      </button>
    );
  };
  const cls = (k: string, extra = '') => `ni-in ${ai.has(k) ? 'ai' : ''} ${hot === k ? 'hot' : ''} ${extra}`;
  const orgOpts = orgs.map((o) => (
    <option key={o.id} value={o.id}>
      {o.name}
    </option>
  ));
  const subs = type === 'notice' ? NOTICE_SUBS : def.subtypes ?? [];
  const showForm = phase === 'filled' || phase === 'manual';
  const heading = type === 'nda' ? f.company || f.title : f.title;
  const total = done + queue.length;

  return (
    <div className="ni-wrap" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="ni" role="dialog" aria-modal="true" aria-label={`New ${def.one}`} style={{ ['--ni-c' as string]: look.color, ['--ni-t' as string]: look.tint }}>
        <div className="ni-band" />
        <div className="ni-head">
          <span className="ni-tile" aria-hidden="true">
            {look.tag}
          </span>
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="k ni-kind">
              New {def.one}
              {total > 1 ? ` · ${Math.min(done + 1, total)} of ${total}` : ''}
              {nextNo ? ` · ${nextNo}` : ''}
            </div>
            <b className="ni-title">{heading || (phase === 'reading' ? 'SWI AI is reading…' : 'Drop a file to start, or fill in by hand')}</b>
          </div>
          <ol className="ni-steps" aria-label="Steps">
            {[
              ['Add the file', phase === 'empty' || phase === 'paste' || phase === 'uploading'],
              ['SWI AI reads', phase === 'reading'],
              ['Check & save', showForm],
            ].map(([label, on], i) => {
              const cur = phase === 'empty' || phase === 'paste' || phase === 'uploading' ? 0 : phase === 'reading' ? 1 : 2;
              return (
                <li key={String(label)} className={i < cur ? 'done' : on ? 'on' : ''} aria-current={on ? 'step' : undefined}>
                  <span className="n">{i < cur ? '✓' : i + 1}</span>
                  {label}
                </li>
              );
            })}
          </ol>
          <button type="button" className="ni-x" aria-label="Close" onClick={onClose} disabled={Boolean(busy)}>
            ×
          </button>
        </div>

        <div className="ni-body">
          {/* the document */}
          <div className="ni-doc">
            {phase === 'empty' || phase === 'uploading' ? (
              <button
                type="button"
                className={`ni-drop ${over ? 'over' : ''}`}
                onClick={() => input.current?.click()}
                onDragOver={(e) => (e.preventDefault(), setOver(true))}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(false);
                  const fs = Array.from(e.dataTransfer.files);
                  if (fs.length) {
                    setQueue(fs);
                    void start(fs[0]);
                  }
                }}
                disabled={Boolean(busy)}
              >
                <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M12 16V4M7 9l5-5 5 5" />
                  <path d="M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" />
                </svg>
                <b>{busy ?? `Drop the ${DROP[type]} or email here`}</b>
                <span>PDF, photo, Word (.docx) or email (.eml). SWI AI reads it and fills in the form — you check and save.</span>
              </button>
            ) : null}
            {phase === 'empty' ? (
              <div className="ni-alt">
                or{' '}
                <button type="button" className="linkbtn" onClick={() => input.current?.click()}>
                  choose a file
                </button>{' '}
                ·{' '}
                <button type="button" className="linkbtn" onClick={() => setPhase('paste')}>
                  paste email text
                </button>{' '}
                ·{' '}
                <button type="button" className="linkbtn" onClick={() => setPhase('manual')}>
                  fill in by hand
                </button>
              </div>
            ) : null}
            {phase === 'paste' ? (
              <div className="ni-paste">
                <label className="field">
                  <span className="field-label">Paste the email or letter text</span>
                  <textarea rows={14} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="Dear Chris, Please find attached…" />
                </label>
                <div className="resp-row">
                  <Button kind="primary" disabled={!paste.trim() || Boolean(busy)} onClick={() => void usePaste()}>
                    Let SWI AI read it
                  </Button>
                  <Button onClick={() => setPhase('empty')}>Back</Button>
                </div>
              </div>
            ) : null}
            {phase === 'reading' ? (
              <div className="ni-reading">
                <div className="ni-sheet">
                  <span className="scan" aria-hidden="true" />
                </div>
                <b className="ni-live c3-inline"><C3Loader size={48} variant="levels" label="SWI AI is reading" /> SWI AI is reading {file?.name}…</b>
                <span className="muted small">Usually under a minute. You can start typing on the right meanwhile.</span>
                <Button onClick={() => setPhase('manual')}>Fill in by hand instead</Button>
              </div>
            ) : null}
            {showForm && file ? (
              <>
                <div className="ni-file">
                  <span className={`ext ${fileTag(file.name).cls}`}>{fileTag(file.name).label}</span>
                  <b className="grow">{file.name}</b>
                  <button type="button" className="linkbtn small" onClick={() => void viewUrl(file.id).then((r) => window.open(r.url, '_blank', 'noopener'))}>
                    Open
                  </button>
                </div>
                {preview && /pdf/i.test(file.mime ?? file.name) ? (
                  <iframe key={page} className="ni-frame" title={`Preview of ${file.name}`} src={`${preview}#page=${page}&view=FitH`} />
                ) : preview ? (
                  <div className="ni-imgbox">
                    <img src={preview} alt={`Photo: ${file.name}`} />
                  </div>
                ) : (
                  <div className="ni-textbox">{read?.letter_text ?? 'No preview for this kind of file — open it to check.'}</div>
                )}
              </>
            ) : null}
            {showForm && !file ? <div className="ni-nofile muted small">No document — filled in by hand. You can still drop a file on the left to attach it.</div> : null}
            <input
              ref={input}
              type="file"
              hidden
              multiple
              accept=".pdf,.docx,.eml,.txt,image/*"
              onChange={(e) => {
                const fs = Array.from(e.target.files ?? []);
                if (fs.length) {
                  setQueue(fs);
                  void start(fs[0]);
                }
                e.target.value = '';
              }}
            />
          </div>

          {/* the form */}
          <div className="ni-form">
            {err ? <div className="banner banner-warn">{err}</div> : null}
            {phase === 'filled' && read ? (
              <div className="ni-claude">
                <div>
                  <b>SWI AI filled {filledN} field{filledN === 1 ? '' : 's'}</b> — shown in purple. Click a field's tag to see the page it came from.
                </div>
                {read.doc_type && read.doc_type !== type && read.doc_type !== 'other' ? <div className="warn-t">This reads like {A_NAME[read.doc_type as ItemType] ?? 'something else'}, not {A_NAME[type]}. {read.doc_type_reason}</div> : read.doc_type_reason ? <div className="muted small">{read.doc_type_reason}</div> : null}
                {!read.respond_by && def.hasRespondBy ? <div className="muted small">Respond-by isn't in the document — set to 7 days.</div> : null}
              </div>
            ) : null}
            {showForm ? (
              dupe ? (
                <div className="ni-chk bad">
                  <b>Already in PM C3?</b> {dupe.number} — {dupe.title.slice(0, 90)}{' '}
                  <a href={`/r/${def.module}/${dupe.id}`} target="_blank" rel="noopener noreferrer">
                    open it
                  </a>
                </div>
              ) : (type === 'nda' ? f.company || f.title : f.ref) ? (
                <div className="ni-chk ok">
                  <b>No duplicate</b> — {type === 'nda' ? 'no other NDA with this company' : `${f.ref} isn't in PM C3 yet`}.
                </div>
              ) : null
            ) : null}

            {phase === 'empty' || phase === 'paste' || phase === 'uploading' || phase === 'reading' ? <p className="muted small">The form fills in as soon as SWI AI has read the file — nothing is saved until you press Save.</p> : null}

            {showForm && type !== 'nda' ? (
              <>
                {subs.length ? (
                <div className="ni-f" role="group" aria-label="Type">
                  <span className="ni-lab">{type === 'notice' ? 'Kind of notice' : 'Type'} {src('subtype')}</span>
                  <div className="chips">
                    {subs.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`chip ${f.subtype === s ? 'on' : ''}`}
                        aria-pressed={f.subtype === s}
                        onClick={() => {
                          set('subtype', s);
                          // an SI goes to the contractor: fill To / onus with PBS when empty
                          if (type === 'rfi' && isSiType(s) && !f.directed_to.length && pbs) setF((x) => ({ ...x, subtype: s, directed_to: [pbs.id], onus_org_id: x.onus_org_id || pbs.id }));
                        }}
                      >
                        {type === 'notice' ? SUB_LABEL(s) : s}
                      </button>
                    ))}
                  </div>
                </div>
                ) : null}
                {siOn ? (
                  <div className="ni-si" role="group" aria-label="Does this SI answer an RFI?">
                    <div className="ni-si-head">
                      <b>Does this SI answer an RFI or an earlier SI?</b> {src('answers', 'answers_refs')}
                      <span className="muted small">Tick the ones it answers — they are closed when you save.</span>
                    </div>
                    <input className="ni-si-search" type="search" value={siQ} onChange={(e) => setSiQ(e.target.value)} placeholder="Search open RFIs / SIs — number, ref or topic" aria-label="Search open RFIs and SIs" />
                    <div className="ni-si-list">
                      {siList.map((i) => {
                        const on = f.answers.includes(i.id);
                        return (
                          <label key={i.id} className={`ni-si-row ${on ? 'on' : ''} ${on && ai.has('answers') ? 'ai' : ''}`}>
                            <input type="checkbox" checked={on} onChange={() => set('answers', on ? f.answers.filter((x) => x !== i.id) : [...f.answers, i.id])} />
                            <b>{i.number}</b>
                            <span className="t">{isSiType(i.subtype) ? 'SI · ' : ''}{i.title}</span>
                            <span className="st">{String(i.data?.['Project RFI #'] ?? i.ref_no ?? '')} · {statusLabel(i.status, 'rfi')}</span>
                          </label>
                        );
                      })}
                      {!siList.length ? <span className="muted small">{siQ ? 'No open RFI or SI matches.' : 'No open RFIs or SIs.'}</span> : null}
                    </div>
                    {!f.answers.length ? (
                      <label className="check small">
                        <input type="checkbox" checked={f.si_on_site} onChange={(e) => set('si_on_site', e.target.checked)} /> An RFI was raised on site (not officially) — track this like an RFI
                      </label>
                    ) : null}
                    <div className="ni-si-flow" aria-live="polite">
                      {f.answers.length ? (
                        <>
                          <b>On save:</b> {f.answers.map((id) => items.find((i) => i.id === id)?.number).filter(Boolean).join(', ')} {f.answers.length === 1 ? 'is' : 'are'} closed as answered by this SI. This SI then waits for {orgName(f.directed_to[0]) ?? 'the contractor'} to acknowledge it (closes it) or come back with a query.
                        </>
                      ) : f.si_on_site ? (
                        <>
                          <b>Tracked like an RFI:</b> raised → sent to architects → awaiting response → answered → closed.
                        </>
                      ) : (
                        <>
                          <b>Issued to {orgName(f.directed_to[0]) ?? 'the contractor'}:</b> one step left — they acknowledge it (closes the SI) or come back with questions / drawings for review, answered by a reply or another SI.
                        </>
                      )}
                    </div>
                  </div>
                ) : null}
                <div className="ni-grid two-one">
                  <label className="ni-f">
                    <span className="ni-lab">
                      Topic <span className="muted">— short, for the register</span> {src('title', 'topic')}
                    </span>
                    <input className={cls('title')} value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Marine plywood thickness at roof" />
                  </label>
                  <label className="ni-f">
                    <span className="ni-lab">{type === 'variation' ? 'VAR ref #' : type === 'claim' ? 'Claim #' : "Contractor's ref"} {src('ref')}</span>
                    <input className={cls('ref')} value={f.ref} onChange={(e) => set('ref', e.target.value)} placeholder={type === 'notice' ? 'e.g. NOPD #4' : type === 'variation' ? 'e.g. VPR 09' : type === 'claim' ? 'e.g. Claim 19' : 'e.g. PBS RFI #27'} />
                  </label>
                </div>
                <label className="ni-f">
                  <span className="ni-lab">
                    {type === 'notice' ? 'Letter text' : money ? 'Description / scope' : 'Question / description'} {src('description', 'letter_text')}
                  </span>
                  <textarea className={cls('description')} rows={5} value={f.description} onChange={(e) => set('description', e.target.value)} />
                </label>
                <div className="ni-f" role="group" aria-label="Buildings">
                  <span className="ni-lab">Buildings {src('building_ids', 'buildings')}</span>
                  <div className={`chips ${ai.has('building_ids') ? 'ni-aichips' : ''}`}>
                    {buildings.map((b) => {
                      const on = f.building_ids.includes(b.id);
                      return (
                        <button key={b.id} type="button" className={`chip ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => set('building_ids', on ? f.building_ids.filter((x) => x !== b.id) : [...f.building_ids, b.id])}>
                          {on ? '✓ ' : ''}
                          {b.short_code ?? b.name}
                        </button>
                      );
                    })}
                  </div>
                  {read && read.buildings.some((b) => !matchName(b, buildings)) ? <span className="muted small">Not matched: {read.buildings.filter((b) => !matchName(b, buildings)).join(', ')} — saved as "to be reviewed".</span> : null}
                </div>
                <div className="ni-grid three">
                  <label className="ni-f">
                    <span className="ni-lab">{type === 'notice' ? 'From' : money ? 'Contractor' : 'Raised by'} {src('org_id', 'from_company')}</span>
                    <select className={cls('org_id')} value={f.org_id} onChange={(e) => set('org_id', e.target.value)}>
                      <option value="">—</option>
                      {orgOpts}
                    </select>
                  </label>
                  <label className="ni-f">
                    <span className="ni-lab">{type === 'notice' ? 'To' : 'Directed to'} {src('directed_to', read?.to_person ? 'to_person' : 'to_company')}</span>
                    <select className={cls('directed_to')} value={f.directed_to[0] ?? ''} onChange={(e) => set('directed_to', e.target.value ? [e.target.value] : [])}>
                      <option value="">—</option>
                      {orgOpts}
                    </select>
                    {read?.to_person && ai.has('directed_to') ? <span className="muted small">"{read.to_person}" matched to a contact</span> : null}
                  </label>
                  <label className="ni-f">
                    <span className="ni-lab">Onus now on</span>
                    <select className={cls('onus_org_id')} value={f.onus_org_id} onChange={(e) => set('onus_org_id', e.target.value)}>
                      <option value="">No one yet</option>
                      {orgOpts}
                    </select>
                  </label>
                  <label className="ni-f">
                    <span className="ni-lab">{type === 'notice' ? 'Letter date' : 'Submitted'} {src('submitted_on', 'doc_date')}</span>
                    <input type="date" className={cls('submitted_on')} value={f.submitted_on} onChange={(e) => (set('submitted_on', e.target.value), !read?.respond_by && e.target.value && set('respond_by', addDays(e.target.value, 7)))} />
                  </label>
                  <label className="ni-f">
                    <span className="ni-lab">Respond by {src('respond_by')}</span>
                    <input type="date" className={cls('respond_by')} value={f.respond_by} onChange={(e) => set('respond_by', e.target.value)} />
                    {!ai.has('respond_by') ? <span className="muted small">7 days unless the document says otherwise</span> : null}
                  </label>
                  {money ? (
                    <label className="ni-f">
                      <span className="ni-lab">
                        Amount (FJD {basis}) {src('amount', basis === 'VEP' ? 'amount_vep' : 'amount_vip')}
                      </span>
                      <input inputMode="decimal" className={cls('amount')} value={f.amount} onChange={(e) => set('amount', e.target.value.replace(/[^\d.,-]/g, ''))} placeholder="0.00" />
                      {otherAmt !== null ? (
                        <span className="muted small">
                          = {fmtMoney(otherAmt)} {basis === 'VEP' ? 'VIP' : 'VEP'} · VAT {fijiVat(f.submitted_on || today())} %
                        </span>
                      ) : null}
                    </label>
                  ) : type === 'rfi' ? (
                    <label className="ni-f">
                      <span className="ni-lab">Drawings referred to {src('drawings')}</span>
                      <input className={cls('drawings')} value={f.drawings} onChange={(e) => set('drawings', e.target.value)} placeholder="e.g. 7/A-502 rev B" />
                    </label>
                  ) : (
                    <label className="ni-f">
                      <span className="ni-lab">Delay since {src('delay_since')}</span>
                      <input type="date" className={cls('delay_since')} value={f.delay_since} onChange={(e) => set('delay_since', e.target.value)} />
                    </label>
                  )}
                </div>
                {type === 'claim' ? (
                  <div className="ni-grid three">
                    <label className="ni-f">
                      <span className="ni-lab">Claim no. {src('claim_no')}</span>
                      <input inputMode="numeric" className={cls('claim_no')} value={f.claim_no} onChange={(e) => set('claim_no', e.target.value.replace(/\D/g, ''))} placeholder="e.g. 19" />
                    </label>
                    <label className="ni-f">
                      <span className="ni-lab">Period to {src('period_to')}</span>
                      <input type="date" className={cls('period_to')} value={f.period_to} onChange={(e) => set('period_to', e.target.value)} />
                    </label>
                    <label className="ni-f">
                      <span className="ni-lab">Contract {src('contract_id', 'contract_ref')}</span>
                      <select className={cls('contract_id')} value={f.contract_id} onChange={(e) => set('contract_id', e.target.value)}>
                        <option value="">Link later on the claim page</option>
                        {contracts.map((c) => (
                          <option key={c.id} value={c.id}>
                            {[c.contract_no, c.title].filter(Boolean).join(' · ') || 'Contract'}
                          </option>
                        ))}
                      </select>
                      {f.contract_id ? (
                        <span className="ni-checkrow small">
                          <input type="checkbox" checked={f.deposit} onChange={(e) => set('deposit', e.target.checked)} /> Deposit claim (advance payment)
                        </span>
                      ) : null}
                    </label>
                  </div>
                ) : null}
                {type === 'notice' ? (
                  <div className="ni-grid three">
                    <label className="ni-f">
                      <span className="ni-lab">Possible EOT {src('possible_eot')}</span>
                      <span className="ni-checkrow">
                        <input type="checkbox" checked={f.possible_eot} onChange={(e) => set('possible_eot', e.target.checked)} /> Warns of / claims an extension of time
                      </span>
                    </label>
                    <label className="ni-f">
                      <span className="ni-lab">EOT days claimed {src('eot_days_claimed')}</span>
                      <input inputMode="numeric" className={cls('eot_days_claimed')} value={f.eot_days_claimed} onChange={(e) => set('eot_days_claimed', e.target.value.replace(/\D/g, ''))} placeholder="none yet" />
                    </label>
                  </div>
                ) : null}
                {type === 'notice' && suggestions.length ? (
                  <div className="ni-f" role="group" aria-label="Related items">
                    <span className="ni-lab">Link to the items this depends on — suggestions</span>
                    {suggestions.map((s) => {
                      const on = f.related.includes(s.id);
                      return (
                        <div key={s.id} className={`ni-sug ${on ? 'on' : ''}`}>
                          <span className="tt" style={{ background: TYPE_LOOK[s.type].color }}>
                            {TYPE_LOOK[s.type].tag}
                          </span>
                          <span className="grow">
                            <b>{s.number}</b> {s.title.slice(0, 90)}
                          </span>
                          <Button className="btn-sm" onClick={() => set('related', on ? f.related.filter((x) => x !== s.id) : [...f.related, s.id])}>
                            {on ? 'Linked ✓' : 'Link'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                {read?.attachments.length ? <div className="muted small">Attachments in the document: {read.attachments.join(' · ')} — the whole file is attached to the new item.</div> : null}
                {!money ? (
                  <label className="check small">
                    <input type="checkbox" checked={f.official} onChange={(e) => set('official', e.target.checked)} /> {type === 'rfi' ? 'Official RFI document sent' : 'Official document received'}
                  </label>
                ) : null}
              </>
            ) : null}

            {showForm && type === 'nda' ? (
              <>
                <div className="ni-grid two">
                  <label className="ni-f">
                    <span className="ni-lab">Company {src('company')}</span>
                    <input
                      className={cls('company')}
                      value={f.company}
                      onChange={(e) => {
                        set('company', e.target.value);
                        set('company_org_id', matchName(e.target.value, orgs)?.id ?? '');
                      }}
                      placeholder="e.g. Coastal Joinery (Fiji) Ltd"
                    />
                    {f.company && !f.company_org_id ? <span className="small strong orange-t">New company — added to Organisations when you save</span> : f.company_org_id ? <span className="muted small">Matched: {orgs.find((o) => o.id === f.company_org_id)?.name}</span> : null}
                  </label>
                  <label className="ni-f">
                    <span className="ni-lab">Introduced by / works for {src('org_id', 'from_company')}</span>
                    <select className={cls('org_id')} value={f.org_id} onChange={(e) => set('org_id', e.target.value)}>
                      <option value="">—</option>
                      {orgOpts}
                    </select>
                  </label>
                  <label className="ni-f">
                    <span className="ni-lab">Person who signed {src('signer', 'signer_name')}</span>
                    <input className={cls('signer')} value={f.signer} onChange={(e) => set('signer', e.target.value)} />
                  </label>
                  <label className="ni-f">
                    <span className="ni-lab">Their role {src('signer_role')}</span>
                    <input className={cls('signer_role')} value={f.signer_role} onChange={(e) => set('signer_role', e.target.value)} />
                  </label>
                  <label className="ni-f">
                    <span className="ni-lab">Date signed {src('date_signed')}</span>
                    <input type="date" className={cls('date_signed')} value={f.date_signed} onChange={(e) => set('date_signed', e.target.value)} />
                  </label>
                  <label className="ni-f">
                    <span className="ni-lab">Ends {src('term_end')}</span>
                    <input type="date" className={cls('term_end')} value={f.term_end} onChange={(e) => set('term_end', e.target.value)} />
                  </label>
                </div>
                <label className="ni-f">
                  <span className="ni-lab">Purpose {src('purpose')}</span>
                  <input className={cls('purpose')} value={f.purpose} onChange={(e) => set('purpose', e.target.value)} placeholder="e.g. kitchen joinery — Treehouse (PBS)" />
                </label>
                <div className="ni-f" role="group" aria-label="Buildings">
                  <span className="ni-lab">Buildings</span>
                  <div className="chips">
                    {buildings.map((b) => {
                      const on = f.building_ids.includes(b.id);
                      return (
                        <button key={b.id} type="button" className={`chip ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => set('building_ids', on ? f.building_ids.filter((x) => x !== b.id) : [...f.building_ids, b.id])}>
                          {on ? '✓ ' : ''}
                          {b.short_code ?? b.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <label className="check small">
                  <input type="checkbox" checked={f.signed} onChange={(e) => set('signed', e.target.checked)} /> Signed by both sides {src('signed', 'signed_by_both')}
                </label>
                <p className="muted small" style={{ margin: 0 }}>
                  Sending the client's NDA for signature from PM C3 comes later, once the client's template is uploaded.
                </p>
              </>
            ) : null}

            <div className="ni-foot">
              <span className="muted small grow">
                {nextNo ? (
                  <>
                    Number on save: <b className="ink">{nextNo}</b>
                  </>
                ) : null}
                {file ? ' · the document is attached to the new item' : ''}
              </span>
              {showForm ? (
                <>
                  <Button disabled={Boolean(busy) || !ready} onClick={() => void save('next')}>
                    Save &amp; add another
                  </Button>
                  <Button kind="primary" disabled={Boolean(busy) || !ready} onClick={() => void save('open')}>
                    {busy === 'Saving…' ? <C3Busy text="Saving…" accent /> : queue.length > 1 ? 'Save & next file' : 'Save & open'}
                  </Button>
                </>
              ) : (
                <Button onClick={onClose}>Cancel</Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
